/**
 * Ads API Slice  –  RTK Query endpoints for the Flask Ads backend
 *
 * Base URL: process.env.REACT_APP_ADS_API_URL  (e.g. http://localhost:8292)
 *
 * Endpoints
 * ---------
 *  Ads
 *    createAd            POST  /api/v1/ads/
 *    submitAd            PUT   /api/v1/ads/:id/submit
 *    uploadPayment       PUT   /api/v1/ads/:id/payment
 *    estimateAudience    POST  /api/v1/ads/estimate-audience
 *    serveAd             POST  /api/v1/ads/serve
 *
 *  Impressions
 *    recordImpression    POST  /api/v1/impressions/
 *
 *  Clicks
 *    recordClick         POST  /api/v1/clicks/
 *
 *  Admin
 *    autoReview          POST  /api/v1/admin/review/:id
 *    manualReview        PUT   /api/v1/admin/review/:id/manual
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const ADS_BASE_URL = (
  process.env.REACT_APP_ADS_API_URL ||
  process.env.ADS_FLASK_API_URL ||
  "http://127.0.0.1:5393"
).replace("localhost", "127.0.0.1");

const rawAdsBaseQuery = fetchBaseQuery({
  baseUrl: ADS_BASE_URL,
  // Flask 500s return HTML (<!doctype...); never let JSON.parse throw in the UI
  responseHandler: async (response) => {
    const contentType = response.headers.get("content-type") || "";
    const text = await response.text();
    if (!text) return null;
    if (contentType.includes("application/json")) {
      try {
        return JSON.parse(text);
      } catch {
        return {
          status: "error",
          message: "Invalid JSON from ads API",
          ads: [],
        };
      }
    }
    // HTML / plain-text error pages
    if (text.trimStart().startsWith("<")) {
      return {
        status: "error",
        message:
          response.ok
            ? "Ads API returned HTML instead of JSON"
            : `Ads API error (${response.status}). Is Flask running on ${ADS_BASE_URL}?`,
        ads: [],
      };
    }
    try {
      return JSON.parse(text);
    } catch {
      return { status: "error", message: text.slice(0, 200), ads: [] };
    }
  },
});

const adsUnreachableMessage = () =>
  `Ads API unreachable at ${ADS_BASE_URL}. Start the Flask ads service (cd server/python_ads && python run.py), then retry.`;

const normalizeAdsNetworkError = (error) => {
  const status = error?.status;
  const raw =
    (typeof error?.error === "string" && error.error) ||
    (typeof error?.data?.message === "string" && error.data.message) ||
    "";
  const isNetworkFail =
    status === "FETCH_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    /failed to fetch|networkerror|load failed|econnrefused/i.test(raw);

  if (!isNetworkFail) return error;

  const message = adsUnreachableMessage();
  return {
    status: "CUSTOM_ERROR",
    error: message,
    data: { message, ads: [] },
  };
};

// Swallow network/CORS failures so the rest of the app keeps working
// when the Flask ads process is down or on another port.
const softAdsBaseQuery = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : args?.url || "";
  const method = (
    typeof args === "string" ? "GET" : args?.method || "GET"
  ).toUpperCase();
  // Soft-fail only serve (public banners). Shop stats list must surface errors
  // so "No Ads" is not shown when Flask is simply unreachable.
  const isSoftRead = method === "GET" && url.includes("/ads/serve");
  try {
    const result = await rawAdsBaseQuery(args, api, extraOptions);
    // Treat HTML-wrapped "success" payloads as errors
    if (
      result?.data?.status === "error" &&
      typeof result.data.message === "string" &&
      result.data.message.includes("Ads API")
    ) {
      if (isSoftRead) {
        return { data: { ads: [], status: "error", message: result.data.message } };
      }
      return {
        error: {
          status: "CUSTOM_ERROR",
          error: result.data.message,
          data: result.data,
        },
      };
    }
    if (result.error) {
      const normalized = normalizeAdsNetworkError(result.error);
      // Soft-read: empty payload without marking a long-lived "success" cache
      // when Flask is down (that previously hid ads after it came back up).
      if (isSoftRead) {
        return {
          data: {
            ads: [],
            status: "error",
            message: normalized?.data?.message || "Ads unavailable",
          },
          meta: { adsSoftFail: true },
        };
      }
      return { error: normalized };
    }
    return result;
  } catch (e) {
    if (isSoftRead) {
      return {
        data: { ads: [], status: "error", message: "Ads unavailable" },
        meta: { adsSoftFail: true },
      };
    }
    return {
      error: normalizeAdsNetworkError({
        status: "FETCH_ERROR",
        error: e?.message || "Ads API request failed",
      }),
    };
  }
};

export const adsApiSlice = createApi({
  reducerPath: "adsApi",
  baseQuery: softAdsBaseQuery,
  tagTypes: ["Ad"],
  // Don't hammer Flask ads when the tab regains focus / reconnects.
  refetchOnFocus: false,
  refetchOnReconnect: false,

  endpoints: (builder) => ({
    // ── Ads ─────────────────────────────────────────────────────────────

    /** Create a new ad draft (multipart/form-data) */
    createAd: builder.mutation({
      query: (formData) => ({
        url: "/api/v1/ads/",
        method: "POST",
        body: formData,
        // Let browser set the correct multipart Content-Type boundary
        formData: true,
      }),
      invalidatesTags: ["Ad"],
    }),

    /** Move an ad from 'pending' → 'submitted' */
    submitAd: builder.mutation({
      query: (adId) => ({
        url: `/api/v1/ads/${adId}/submit`,
        method: "PUT",
      }),
      invalidatesTags: ["Ad"],
    }),

    /** Attach payment details to an ad */
    uploadAdPayment: builder.mutation({
      query: ({ adId, paymentData }) => ({
        url: `/api/v1/ads/${adId}/payment`,
        method: "PUT",
        body: paymentData,
      }),
      invalidatesTags: ["Ad"],
    }),

    /** Dynamically count matching users for a targeting profile */
    estimateAudience: builder.mutation({
      query: (targeting) => ({
        url: "/api/v1/ads/estimate-audience",
        method: "POST",
        body: targeting,
      }),
    }),

    /** Return ranked ads matching a visitor profile + placement */
    serveAd: builder.mutation({
      query: ({ visitor, placement, platform, mediaType, adType }) => ({
        url: "/api/v1/ads/serve",
        method: "POST",
        body: { visitor, placement, platform, mediaType, adType },
      }),
    }),

    /**
     * Serve banner/placement ads as a query.
     * Empty results are cached so the UI does not spin/refetch in a loop.
     */
    serveAds: builder.query({
      query: ({
        placement = "banner",
        platform = "",
        adType = "",
        mediaType = "",
        country = "",
        state = "",
        city = "",
        gender = "all",
        age = "",
        dob = "",
        language = "",
        userId = "",
      } = {}) => {
        const params = new URLSearchParams({
          placement,
          platform: platform || "",
          businessType: platform || "",
          adType: adType || "",
          mediaType: mediaType || "",
          country: country || "",
          state: state || "",
          city: city || "",
          gender: gender || "all",
          age: age !== "" && age != null ? String(age) : "",
          dob: dob || "",
          language: language || "",
          userId: userId || "",
          visitorUserId: userId || "",
        });
        return `/api/v1/ads/serve?${params.toString()}`;
      },
      transformResponse: (response) => ({
        ads: Array.isArray(response?.ads)
          ? response.ads
          : response?.ad
            ? [response.ad].flat()
            : [],
        status: response?.status || "success",
        message: response?.message || "",
        ad: response?.ad ?? null,
      }),
      providesTags: (result, error, arg) => [
        { type: "Ad", id: "SERVE" },
        {
          type: "Ad",
          id: `SERVE-${arg?.platform || "all"}-${arg?.placement || "banner"}`,
        },
      ],
      // Short cache — empty results from Flask-down / strict filters were
      // sticking for minutes and making ShopAdds look permanently blank.
      keepUnusedDataFor: 30,
    }),

    /**
     * List ads owned by the logged-in user only (ownerUserId).
     * Optional shopId filters further by the shop/profile used at create time.
     */
    getAdsByUser: builder.query({
      query: (arg) => {
        const ownerUserId =
          typeof arg === "string" ? arg : arg?.ownerUserId || "";
        const shopId =
          typeof arg === "string" ? undefined : arg?.shopId || arg?.userId;
        const params = {};
        // Shop stats: prefer shop id (stored as userId on the ad document)
        if (shopId) params.userId = shopId;
        if (ownerUserId) params.ownerUserId = ownerUserId;
        return { url: "/api/v1/ads/", params };
      },
      transformResponse: (response) =>
        Array.isArray(response?.ads) ? response.ads : [],
      providesTags: (result, error, arg) => {
        const id =
          typeof arg === "string"
            ? arg
            : arg?.shopId || arg?.ownerUserId || arg?.userId;
        const tags = [
          { type: "Ad", id: "STATS" },
          { type: "Ad", id: `USER-${id}` },
        ];
        if (Array.isArray(result)) {
          result.forEach((ad) => {
            if (ad?._id) tags.push({ type: "Ad", id: ad._id });
          });
        }
        return tags;
      },
    }),

    // ── Impressions ──────────────────────────────────────────────────────

    /** Record that a visitor has seen an ad (Impressions +1, daily budget) */
    recordImpression: builder.mutation({
      query: (arg) => {
        const adId = typeof arg === "string" ? arg : arg?.adId;
        const visitorUserId =
          typeof arg === "string" ? undefined : arg?.visitorUserId;
        const page = typeof arg === "string" ? undefined : arg?.page;
        const country = typeof arg === "string" ? undefined : arg?.country;
        return {
          url: "/api/v1/impressions/",
          method: "POST",
          body: {
            adId,
            ...(visitorUserId ? { visitorUserId } : {}),
            ...(page ? { page } : {}),
            ...(country ? { country } : {}),
          },
        };
      },
      // Refresh shop stats lists only — do not bust serveAds cache every view
      invalidatesTags: (result, error, arg) => {
        const adId = typeof arg === "string" ? arg : arg?.adId;
        // Owner self-views should not refetch stats
        if (result?.skipped) return [];
        return [
          { type: "Ad", id: "STATS" },
          { type: "Ad", id: adId },
        ];
      },
    }),

    // ── Clicks ───────────────────────────────────────────────────────────

    /** Record a click (Clicks +1); returns redirect URL */
    recordClick: builder.mutation({
      query: (arg) => {
        const adId = typeof arg === "string" ? arg : arg?.adId;
        const visitorUserId =
          typeof arg === "string" ? undefined : arg?.visitorUserId;
        const country = typeof arg === "string" ? undefined : arg?.country;
        return {
          url: "/api/v1/clicks/",
          method: "POST",
          body: {
            adId,
            ...(visitorUserId ? { visitorUserId } : {}),
            ...(country ? { country } : {}),
          },
        };
      },
      invalidatesTags: (result, error, arg) => {
        const adId = typeof arg === "string" ? arg : arg?.adId;
        if (result?.skipped) return [];
        return [
          { type: "Ad", id: "STATS" },
          { type: "Ad", id: adId },
        ];
      },
    }),

    // ── Admin Review ─────────────────────────────────────────────────────

    /** Auto admin review: payment + content check → approve / reject */
    autoReviewAd: builder.mutation({
      query: ({ adId, adminNotes = "" }) => ({
        url: `/api/v1/admin/review/${adId}`,
        method: "POST",
        body: { adminNotes },
      }),
      invalidatesTags: ["Ad"],
    }),

    /** Manual admin decision: 'approve' or 'reject' */
    manualReviewAd: builder.mutation({
      query: ({ adId, decision, adminNotes = "" }) => ({
        url: `/api/v1/admin/review/${adId}/manual`,
        method: "PUT",
        body: { decision, adminNotes },
      }),
      invalidatesTags: ["Ad"],
    }),
  }),
});

export const {
  useCreateAdMutation,
  useSubmitAdMutation,
  useUploadAdPaymentMutation,
  useEstimateAudienceMutation,
  useServeAdMutation,
  useServeAdsQuery,
  useLazyServeAdsQuery,
  useGetAdsByUserQuery,
  useRecordImpressionMutation,
  useRecordClickMutation,
  useAutoReviewAdMutation,
  useManualReviewAdMutation,
} = adsApiSlice;
